'use client';

import type { CropArea, ResizeHandle, ImageNaturalSize } from './types';

interface CropOverlayProps {
  cropArea: CropArea;
  imageNaturalSize: ImageNaturalSize;
  imageRect: DOMRect;
  onMouseDown: (e: React.MouseEvent, handle?: ResizeHandle) => void;
  onTouchStart: (e: React.TouchEvent, handle?: ResizeHandle) => void;
}

interface HandleProps {
  handle: ResizeHandle;
  position: string;
  cursor: string;
  onMouseDown: (e: React.MouseEvent, handle?: ResizeHandle) => void;
  onTouchStart: (e: React.TouchEvent, handle?: ResizeHandle) => void;
}

function CropHandle({
  handle,
  position,
  cursor,
  onMouseDown,
  onTouchStart,
}: HandleProps) {
  return (
    <div
      className={`absolute ${position} w-8 h-8 md:w-4 md:h-4 bg-white border-2 border-muted-foreground ${cursor} touch-none rounded-full md:rounded-none shadow-lg`}
      onMouseDown={(e) => onMouseDown(e, handle)}
      onTouchStart={(e) => onTouchStart(e, handle)}
    />
  );
}

export function CropOverlay({
  cropArea,
  imageNaturalSize,
  imageRect,
  onMouseDown,
  onTouchStart,
}: CropOverlayProps) {
  const scaleX = imageRect.width / imageNaturalSize.width;
  const scaleY = imageRect.height / imageNaturalSize.height;

  const overlayStyle = {
    left: `${cropArea.x * scaleX}px`,
    top: `${cropArea.y * scaleY}px`,
    width: `${cropArea.width * scaleX}px`,
    height: `${cropArea.height * scaleY}px`,
  };

  return (
    <>
      {/* Dark overlay outside crop area */}
      <div
        className="absolute pointer-events-none"
        style={{
          boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.5)`,
          ...overlayStyle,
        }}
      />

      {/* Crop area with handles */}
      <div
        className="absolute border-2 border-white cursor-move touch-none"
        style={overlayStyle}
        onMouseDown={(e) => onMouseDown(e)}
        onTouchStart={(e) => onTouchStart(e)}
      >
        {/* Corner handles */}
        <CropHandle
          handle="nw"
          position="top-0 left-0 -translate-x-1/2 -translate-y-1/2"
          cursor="cursor-nw-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <CropHandle
          handle="ne"
          position="top-0 right-0 translate-x-1/2 -translate-y-1/2"
          cursor="cursor-ne-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <CropHandle
          handle="sw"
          position="bottom-0 left-0 -translate-x-1/2 translate-y-1/2"
          cursor="cursor-sw-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <CropHandle
          handle="se"
          position="bottom-0 right-0 translate-x-1/2 translate-y-1/2"
          cursor="cursor-se-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />

        {/* Edge handles */}
        <CropHandle
          handle="n"
          position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"
          cursor="cursor-n-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <CropHandle
          handle="s"
          position="bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2"
          cursor="cursor-s-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <CropHandle
          handle="w"
          position="top-1/2 left-0 -translate-x-1/2 -translate-y-1/2"
          cursor="cursor-w-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
        <CropHandle
          handle="e"
          position="top-1/2 right-0 translate-x-1/2 -translate-y-1/2"
          cursor="cursor-e-resize"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />

        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white opacity-30" />
          <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white opacity-30" />
          <div className="absolute top-1/3 left-0 right-0 h-px bg-white opacity-30" />
          <div className="absolute top-2/3 left-0 right-0 h-px bg-white opacity-30" />
        </div>
      </div>
    </>
  );
}
